import React, { useEffect, useMemo, useState } from "react";
import { getAttendance } from "../../services/api";

import { FiEye, FiMapPin, FiSearch, FiExternalLink } from "react-icons/fi";
import GroupsIcon from "@mui/icons-material/Groups";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LoginIcon from "@mui/icons-material/Login";
import globalStyles, { COLORS } from "../../styles/globalStyles";

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);

  return isMobile;
};

const getTodayString = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatDateTime = (date) => {
  if (!date) return "—";

  return new Date(date).toLocaleString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const getStatusType = (record) => {
  if (!record?.checkInTime) return "missing";
  if (record?.checkOutTime) return "completed";
  return "active";
};

const getStatusLabel = (record) => {
  if (!record?.checkInTime) return "Missing";
  if (record?.checkOutTime) return "Completed";
  return "Checked In";
};

const getUserName = (record) => {
  if (typeof record?.userId === "object") return record.userId?.name || "—";
  return record?.userName || record?.name || "—";
};

const getEmployeeId = (record) => {
  if (typeof record?.userId === "object") {
    return record.userId?.employeeId || "—";
  }

  return record?.employeeId || "—";
};

const AttendanceTile = ({
  value,
  label,
  icon: Icon,
  color,
  active,
  onClick,
}) => (
  <div
    style={{ ...globalStyles.tile, ...(active ? globalStyles.activeTile : {}) }}
    onClick={onClick}
  >
    <div style={globalStyles.tileIcon(color)}>
      <Icon fontSize="medium" />
    </div>

    <div style={globalStyles.tileText}>
      <div style={globalStyles.tileValue}>{value ?? 0}</div>
      <div style={globalStyles.tileLabel}>{label}</div>
    </div>
  </div>
);

export default function Attendance() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [view, setView] = useState(null);
  const [mapData, setMapData] = useState(null);

  const [page, setPage] = useState(1);
  const perPage = 10;
  const isMobile = useIsMobile();

  const loadRecords = async () => {
    setLoading(true);
    setMsg("");

    try {
      const attendanceResponse = await getAttendance({ limit: 500 });

      const attendanceRecords =
        attendanceResponse?.data?.data?.records ||
        attendanceResponse?.data?.records ||
        attendanceResponse?.data?.data ||
        [];

      setRecords(Array.isArray(attendanceRecords) ? attendanceRecords : []);
    } catch (error) {
      setMsg(error?.response?.data?.message || "Failed to load attendance");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecords();
  }, []);

  const todayStr = useMemo(() => getTodayString(), []);

  const isPresentTodaySelected =
    dateFilter === todayStr && statusFilter === "present";

  const tileBaseRecords = useMemo(() => {
    if (isPresentTodaySelected) return records;

    return dateFilter
      ? records.filter((record) => record.date === dateFilter)
      : records;
  }, [records, dateFilter, isPresentTodaySelected]);

  const todayRecords = useMemo(() => {
    return records.filter((record) => record.date === todayStr);
  }, [records, todayStr]);

  const totalRecords = tileBaseRecords.length;

  const presentTodayRecords = todayRecords.filter(
    (record) => record.checkInTime,
  ).length;

  const completedRecords = tileBaseRecords.filter(
    (record) => record.checkInTime && record.checkOutTime,
  ).length;

  const checkedInRecords = tileBaseRecords.filter(
    (record) => record.checkInTime && !record.checkOutTime,
  ).length;

  const filteredRecords = useMemo(() => {
    return records.filter((record) => {
      const locationText = record.location
        ? `${record.location.latitude}, ${record.location.longitude}`
        : "";

      const keyword = search.toLowerCase();

      const matchSearch =
        getUserName(record).toLowerCase().includes(keyword) ||
        getEmployeeId(record).toLowerCase().includes(keyword) ||
        locationText.toLowerCase().includes(keyword);

      const matchDate = dateFilter ? record.date === dateFilter : true;

      const statusType = getStatusType(record);

      const matchStatus = statusFilter
        ? statusFilter === "present"
          ? statusType === "active" || statusType === "completed"
          : statusType === statusFilter
        : true;

      return matchSearch && matchDate && matchStatus;
    });
  }, [records, search, dateFilter, statusFilter]);

  const totalPages = Math.ceil(filteredRecords.length / perPage) || 1;

  const paginatedRecords = filteredRecords
    .slice()
    .sort((a, b) => {
      const aTime = new Date(a.checkInTime || a.createdAt || 0).getTime();
      const bTime = new Date(b.checkInTime || b.createdAt || 0).getTime();
      return bTime - aTime;
    })
    .slice((page - 1) * perPage, page * perPage);

  if (loading) {
    return <div style={globalStyles.loading}>Loading attendance...</div>;
  }

  return (
    <div style={globalStyles.contentWrapper}>
      <div style={globalStyles.row}>
        <h1 style={globalStyles.h1}>Attendance</h1>
      </div>

      <div
        style={{
          ...globalStyles.tilesGrid,
          ...(isMobile ? globalStyles.responsive.mobileTilesGrid : {}),
        }}
      >
        <AttendanceTile
          value={totalRecords}
          label="Total Records"
          icon={GroupsIcon}
          color="#06b6d4"
          active={dateFilter === "" && statusFilter === ""}
          onClick={() => {
            setDateFilter("");
            setStatusFilter("");
            setPage(1);
          }}
        />

        <AttendanceTile
          value={presentTodayRecords}
          label="Present Today"
          icon={CheckCircleIcon}
          color="#2563eb"
          active={dateFilter === todayStr && statusFilter === "present"}
          onClick={() => {
            setDateFilter(todayStr);
            setStatusFilter("present");
            setPage(1);
          }}
        />

        <AttendanceTile
          value={completedRecords}
          label="Completed"
          icon={CheckCircleIcon}
          color="#10b981"
          active={dateFilter === "" && statusFilter === "completed"}
          onClick={() => {
            setDateFilter("");
            setStatusFilter("completed");
            setPage(1);
          }}
        />

        <AttendanceTile
          value={checkedInRecords}
          label="Checked In"
          icon={LoginIcon}
          color="#f59e0b"
          active={dateFilter === "" && statusFilter === "active"}
          onClick={() => {
            setDateFilter("");
            setStatusFilter("active");
            setPage(1);
          }}
        />
      </div>

      <div>
        {msg && <div style={globalStyles.errorBanner}>{msg}</div>}

        <div
          style={{
            ...globalStyles.toolbar,
            ...(isMobile ? globalStyles.responsive.mobileToolbar : {}),
          }}
        >
          <div
            style={{
              ...globalStyles.filterLeft,
              ...(isMobile ? globalStyles.responsive.mobileFilterLeft : {}),
            }}
          >
            <div
              style={{
                ...globalStyles.searchWrap,
                ...(isMobile ? globalStyles.responsive.mobileSearchWrap : {}),
              }}
            >
              <FiSearch style={globalStyles.searchIcon} />

              <input
                style={globalStyles.searchInput}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search by user, employee id, location..."
              />
            </div>

            <input
              type="date"
              style={{
                ...globalStyles.filterSelect,
                ...(isMobile ? globalStyles.responsive.mobileFilterSelect : {}),
              }}
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                setPage(1);
              }}
            />

            <select
              style={{
                ...globalStyles.filterSelect,
                ...(isMobile ? globalStyles.responsive.mobileFilterSelect : {}),
              }}
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Status</option>
              <option value="present">Present</option>
              <option value="active">Checked In</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        <div style={globalStyles.tableScroll}>
          <table style={globalStyles.table}>
            <thead>
              <tr>
                <th style={globalStyles.th}>User</th>
                <th style={globalStyles.th}>Employee ID</th>
                <th style={globalStyles.th}>Date</th>
                <th style={globalStyles.th}>Check In</th>
                <th style={globalStyles.th}>Check Out</th>
                <th style={globalStyles.th}>Status</th>
                <th style={globalStyles.th}>Location</th>
                <th style={globalStyles.th}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {paginatedRecords.length > 0 ? (
                paginatedRecords.map((record) => {
                  const hasLocation =
                    record.location?.latitude && record.location?.longitude;

                  return (
                    <tr key={record._id}>
                      <td style={globalStyles.td}>
                        <strong>{getUserName(record)}</strong>
                      </td>

                      <td style={globalStyles.td}>{getEmployeeId(record)}</td>

                      <td style={globalStyles.td}>{record.date || "—"}</td>

                      <td style={globalStyles.td}>
                        {formatDateTime(record.checkInTime)}
                      </td>

                      <td style={globalStyles.td}>
                        {formatDateTime(record.checkOutTime)}
                      </td>

                      <td style={globalStyles.td}>
                        <span style={globalStyles.badge(getStatusType(record))}>
                          {getStatusLabel(record)}
                        </span>
                      </td>

                      <td style={globalStyles.td}>
                        {hasLocation ? (
                          <button
                            type="button"
                            title={`${record.location.latitude}, ${record.location.longitude}`}
                            style={globalStyles.mapBtn}
                            onClick={() => setMapData(record.location)}
                          >
                            <FiMapPin size={14} />
                          </button>
                        ) : (
                          "—"
                        )}
                      </td>

                      <td style={globalStyles.td}>
                        <div style={globalStyles.btnRow}>
                          <button
                            type="button"
                            title="View"
                            style={globalStyles.viewBtn}
                            onClick={() => setView(record)}
                          >
                            <FiEye size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    style={{ ...globalStyles.td, ...globalStyles.emptyState }}
                  >
                    No attendance records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div style={globalStyles.paginateRow}>
            <button
              type="button"
              style={{
                ...globalStyles.paginateBtn,
                opacity: page === 1 ? 0.5 : 1,
              }}
              disabled={page === 1}
              onClick={() => setPage((prev) => prev - 1)}
            >
              ‹
            </button>

            <span style={globalStyles.paginatePageInfo}>
              Page {page} of {totalPages}
            </span>

            <button
              type="button"
              style={{
                ...globalStyles.paginateBtn,
                opacity: page === totalPages ? 0.5 : 1,
              }}
              disabled={page === totalPages}
              onClick={() => setPage((prev) => prev + 1)}
            >
              ›
            </button>
          </div>
        )}
      </div>

      {view && (
        <div style={globalStyles.modal}>
          <div
            style={{
              ...globalStyles.mcard,
              ...(isMobile ? globalStyles.responsive.mobileModalCard : {}),
            }}
          >
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>
              View Attendance
            </h2>

            <div
              style={{
                ...globalStyles.viewGrid,
                ...(isMobile ? { gridTemplateColumns: "1fr" } : {}),
              }}
            >
              <div style={globalStyles.viewItem}>
                <div style={globalStyles.viewLabel}>User</div>
                <div style={globalStyles.viewValue}>{getUserName(view)}</div>
              </div>

              <div style={globalStyles.viewItem}>
                <div style={globalStyles.viewLabel}>Employee ID</div>
                <div style={globalStyles.viewValue}>{getEmployeeId(view)}</div>
              </div>

              <div style={globalStyles.viewItem}>
                <div style={globalStyles.viewLabel}>Date</div>
                <div style={globalStyles.viewValue}>{view.date || "—"}</div>
              </div>

              <div style={globalStyles.viewItem}>
                <div style={globalStyles.viewLabel}>Status</div>
                <div style={globalStyles.viewValue}>
                  <span style={globalStyles.badge(getStatusType(view))}>
                    {getStatusLabel(view)}
                  </span>
                </div>
              </div>

              <div style={globalStyles.viewItem}>
                <div style={globalStyles.viewLabel}>Check In Time</div>
                <div style={globalStyles.viewValue}>
                  {formatDateTime(view.checkInTime)}
                </div>
              </div>

              <div style={globalStyles.viewItem}>
                <div style={globalStyles.viewLabel}>Check Out Time</div>
                <div style={globalStyles.viewValue}>
                  {formatDateTime(view.checkOutTime)}
                </div>
              </div>

              <div style={globalStyles.viewItem}>
                <div style={globalStyles.viewLabel}>Latitude</div>
                <div style={globalStyles.viewValue}>
                  {view.location?.latitude ?? "—"}
                </div>
              </div>

              <div style={globalStyles.viewItem}>
                <div style={globalStyles.viewLabel}>Longitude</div>
                <div style={globalStyles.viewValue}>
                  {view.location?.longitude ?? "—"}
                </div>
              </div>

              <div style={globalStyles.viewItemFull}>
                <div style={globalStyles.viewLabel}>Created At</div>
                <div style={globalStyles.viewValue}>
                  {formatDateTime(view.createdAt)}
                </div>
              </div>

              <div style={globalStyles.viewItemFull}>
                <div style={globalStyles.viewLabel}>Updated At</div>
                <div style={globalStyles.viewValue}>
                  {formatDateTime(view.updatedAt)}
                </div>
              </div>
            </div>

            <div
              style={{
                ...globalStyles.mrow,
                ...(isMobile ? { justifyContent: "stretch" } : {}),
              }}
            >
              <button
                type="button"
                style={{
                  ...globalStyles.cancel,
                  ...(isMobile ? { width: "100%" } : {}),
                }}
                onClick={() => setView(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {mapData && (
        <div style={globalStyles.mapOverlay}>
          <div
            style={{
              ...globalStyles.mapCard,
              ...(isMobile ? { width: "94vw", padding: 14 } : {}),
            }}
          >
            <div style={globalStyles.mapHeader}>
              <div>
                <div style={globalStyles.mapTitle}>Location Coordinates</div>

                <div style={globalStyles.mapCoords}>
                  {mapData.latitude}, {mapData.longitude}
                </div>
              </div>

              <button
                type="button"
                style={globalStyles.closeBtn}
                onClick={() => setMapData(null)}
              >
                ✕
              </button>
            </div>

            <iframe
              title="map"
              width="100%"
              height="260"
              style={globalStyles.mapFrame}
              loading="lazy"
              src={`https://maps.google.com/maps?q=${mapData.latitude},${mapData.longitude}&z=15&output=embed`}
            />

            <button
              type="button"
              style={globalStyles.mapActionBtn}
              onClick={() =>
                window.open(
                  `https://www.google.com/maps?q=${mapData.latitude},${mapData.longitude}`,
                  "_blank",
                )
              }
            >
              <FiExternalLink size={14} />
              Open in Google Maps
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
